import {DSVRowArray, DSVRowString} from "d3";

export class DashboardFilterApplicator<FV, D extends DSVRowArray, R extends Object> {
  rowPassesFilter(row: R, filterValue: FV): boolean {
    return true;
  }

  applyFilter(data: D, filterValue: FV): D {
    return data;
  }

  dataRefreshFromRemoteNecessary(filterValue: FV, previousFilterValue: FV | undefined): boolean {
    return false;
  }
}


export class LocalRowColumnSingleFilterValueFilterApplicator extends DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString> {
  columnKey: string;
  bypassCallback: (row: DSVRowString, filterValue: string | number) => boolean;

  constructor(columnKey: string, bypassCallback: (row: DSVRowString, filterValue: string | number) => boolean = () => false) {
    super();
    this.columnKey = columnKey;
    this.bypassCallback = bypassCallback;
  }

  override rowPassesFilter(row: DSVRowString, filterValue: string | number): boolean {
    if (this.bypassCallback(row, filterValue)) {
      return true;
    }
    if (row[this.columnKey] == null) {
      console.warn(`No Value for column "${this.columnKey}" in row ${row}`)
      return true;
    }
    return row[this.columnKey] === filterValue || row[this.columnKey] === filterValue.toString()
  }
}


export class LocalRowColumnArrayFilterValueFilterApplicator extends DashboardFilterApplicator<(string | number)[], DSVRowArray<string>, DSVRowString> {
  columnKey: string;
  bypassCallback: (row: DSVRowString, filterValue: (string | number)[]) => boolean;

  constructor(columnKey: string, bypassCallback: (row: DSVRowString, filterValue: (string | number)[]) => boolean = () => false) {
    super();
    this.columnKey = columnKey;
    this.bypassCallback = bypassCallback;
  }

  override rowPassesFilter(row: DSVRowString, filterValues: string[]): boolean {
    if (this.bypassCallback(row, filterValues)) {
      return true;
    }
    if (row[this.columnKey] == null) {
      console.warn(`No Value for column "${this.columnKey}" in row ${row}`)
      return true;
    }
    const cell = `${row[this.columnKey]}`;
    return filterValues.some((v) => `${v}` === cell);
  }
}


export class RemoteFilterApplicator extends DashboardFilterApplicator<any, DSVRowArray<string>, DSVRowString> {
  remoteFilterKey: string;

  constructor({remoteFilterKey}: { remoteFilterKey: string }) {
    super();
    this.remoteFilterKey = remoteFilterKey;
  };

  override dataRefreshFromRemoteNecessary(filterValue: any, previousFilterValue: any | undefined): boolean {
    return filterValue !== previousFilterValue;
  }
}


export class FilterValueToColumnApplicator extends DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString> {
  columnKey: string;

  constructor(columnKey: string) {
    super();
    this.columnKey = columnKey;
  }

  override applyFilter(data: DSVRowArray<string>, filterValue: string): DSVRowArray<string> {
    return data.map(row => {
      row[this.columnKey] = filterValue;
      return row;
    }) as DSVRowArray<string>;
  }
}


export class LocalRowColumnValueInRangeFilterApplicator extends DashboardFilterApplicator<number[], DSVRowArray<string>, DSVRowString> {
  columnKey: string;

  constructor(columnKey: string) {
    super();
    this.columnKey = columnKey;
  }

  override rowPassesFilter(row: DSVRowString, filterValue: number[]): boolean {
    if (row[this.columnKey] == null) {
      console.warn(`No Value for column "${this.columnKey}" in row ${row}`)
      return true;
    }
    return filterValue[0] <= +row[this.columnKey]! && +row[this.columnKey]! <= filterValue[1];
  }
}

export class LocalRowPassesFilterApplicatorResultToColumnApplicator<FV> extends DashboardFilterApplicator<FV, DSVRowArray<string>, DSVRowString> {

  columnKey: string;
  applicator: DashboardFilterApplicator<FV, DSVRowArray<string>, DSVRowString>;

  constructor(resultColumnKey: string, applicator: DashboardFilterApplicator<any, DSVRowArray<string>, DSVRowString>) {
    super();
    this.columnKey = resultColumnKey;
    this.applicator = applicator;
  }

  override applyFilter(data: DSVRowArray<string>, filterValue: FV): DSVRowArray<string> {
    return data.map(row => {
      const rowPassedFilter = this.applicator.rowPassesFilter(row, filterValue);
      row[this.columnKey] = rowPassedFilter ? 'true' : 'false';
      return row;
    }) as DSVRowArray<string>;
  }
}

export class PeriodTypeFilterValueFilterApplicator extends DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString> {
  override rowPassesFilter(row: DSVRowString, filterValue: 'year' | 'quarter' | 'month'): boolean {
    if (row['period'] == null) {
      console.warn(`No Value for column 'period' in row ${row}`)
      return true;
    }

    if (filterValue === 'year') {
      return row['quarter'] === '2'
    }

    return row['period'] === filterValue
  }
}

const isFourDigitNumber = (str: string) => /^\d{4}$/.test(str);

export class PeriodValueFilterValueFilterApplicator extends DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString> {

  override rowPassesFilter(row: DSVRowString, filterValue: string): boolean {
    if (row['period_id'] == null) {
      console.warn(`No Value for column 'period_value' in row ${row}`)
      return true;
    }

    if (isFourDigitNumber(filterValue)) {
      return row['year'] === filterValue
    }

    return row['period_id'] === filterValue
  }
}


export class PeriodRangeFilterValueFilterApplicator extends DashboardFilterApplicator<string[], DSVRowArray<string>, DSVRowString> {
  override rowPassesFilter(row: DSVRowString, filterValues: string[]): boolean {
    if (row['period_id'] == null) {
      console.warn(`No Value for column 'period_id' in row ${row}`)
      return true;
    }

    // Check for the years values
    if (isFourDigitNumber(filterValues[0])) {
      return filterValues.includes(row['year'])
    }

    return filterValues.includes(row['period_id']!);
  }
}

